// services/support.service.js
import assistantClient from '../api/assistantClient';

const SUPPORT_ASSISTANT_ID = process.env.REACT_APP_SUPPORT_ASSISTANT_ID;

/**
 * Servicio para el chat con el Bot de Soporte de Zenit.
 *
 * Usa el endpoint dedicado `/accounts/{accountId}/support/chat`, autenticando con
 * el JWT del usuario (assistantClient). El backend guarda la conversación bajo la
 * cuenta del propio usuario (aislada por cuenta) y dirige el cobro a la org (gratis
 * para el usuario). La conversación se crea en el primer mensaje, con título por IA.
 */
class SupportService {
  get isConfigured() {
    return Boolean(SUPPORT_ASSISTANT_ID);
  }

  /**
   * Lista los casos (chat histories) del usuario con el bot de soporte, dentro de
   * su cuenta actual.
   * @param {Object} params
   * @param {string} params.accountId - cuenta actual del usuario
   * @param {number} [params.limit=50]
   */
  async listCases({ accountId, limit = 50 }) {
    try {
      if (!accountId) return { success: true, cases: [] };
      const response = await assistantClient.get(`/accounts/${accountId}/chat/histories`, {
        params: { assistant_id: SUPPORT_ASSISTANT_ID, limit, offset: 0 },
      });
      const cases =
        response.data?.chatHistories ||
        response.data?.chat_histories ||
        (Array.isArray(response.data) ? response.data : []);
      return { success: true, cases: Array.isArray(cases) ? cases : [] };
    } catch (error) {
      return { success: false, error: this._msg(error) };
    }
  }

  /**
   * Trae los mensajes de un caso, normalizados a { role, text }.
   * @param {Object} params
   * @param {string} params.chatHistoryId
   */
  async getMessages({ chatHistoryId, limit = 100 }) {
    try {
      const response = await assistantClient.get(
        `/chat/histories/${chatHistoryId}/messages`,
        { params: { limit, offset: 0 } }
      );
      const raw = response.data?.messages || [];
      const messages = raw
        .map((m) => ({
          role: (m.role || '').toLowerCase() === 'user' ? 'user' : 'assistant',
          text: this._extractText(m),
        }))
        .filter((m) => m.text);
      return { success: true, messages };
    } catch (error) {
      return { success: false, error: this._msg(error) };
    }
  }

  /**
   * Envía un mensaje al bot de soporte. Si `chatHistoryId` es null, el backend crea
   * la conversación (bajo la cuenta del usuario, con título por IA) y devuelve su id.
   * @param {Object} params
   * @param {string} params.accountId - cuenta actual del usuario
   * @param {string} params.userId
   * @param {?string} params.chatHistoryId - id existente o null para crear
   * @param {string} params.text
   * @returns {Promise<{success:boolean, reply?:string, chatHistoryId?:string, error?:string}>}
   */
  async sendMessage({ accountId, userId, chatHistoryId, text }) {
    try {
      const body = { userId, message: text };
      if (chatHistoryId) body.chatHistoryId = chatHistoryId;

      const response = await assistantClient.post(
        `/accounts/${accountId}/support/chat`,
        body
      );
      const data = response.data || {};
      return {
        success: true,
        reply: this._extractText(data) || data.response || '',
        chatHistoryId: data.chatHistoryId || data.chat_history_id || chatHistoryId || null,
      };
    } catch (error) {
      return { success: false, error: this._msg(error) };
    }
  }

  // Extrae el texto de la respuesta del assistant (varios shapes posibles).
  _extractText(data) {
    if (!data) return '';
    if (typeof data.response === 'string') return data.response;
    if (typeof data.content === 'string') return data.content;
    if (typeof data.message === 'string') return data.message;
    const blocks = data.content_blocks || data.contentBlocks;
    if (Array.isArray(blocks)) {
      return blocks
        .filter((b) => (b.type || '').toLowerCase() === 'text')
        .map((b) => b.data?.text || '')
        .join('\n')
        .trim();
    }
    return '';
  }

  _msg(error) {
    const d = error?.response?.data?.detail;
    if (d && typeof d === 'object') return d.message || d.code || 'Support error';
    return d || error?.response?.data?.message || error?.message || 'Support error';
  }
}

export default new SupportService();
